
import { Box, Dialog, DialogTitle, DialogContent, Typography, ListItem, List, Button, Table, TableBody, TableCell, TableHead, TableRow, DialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloseDialogAction } from '../../../components/shared/dialogs/CloseDialogAction';
import DragDropFileUpload from '../../../components/shared/file-upload/DragDropFileUpload';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { useState } from 'react';
export interface ColumnDefinition<T> {
  header: string;
  accessor: (item: T) => string | number;
}
export interface AutoshipUploadFileModalProps<T> {
  open: boolean;
  onClose: () => void;
  onFileSelected?: (file: File) => Promise<{ success: boolean; data?: T[] }>;
  onConfirm?: (items: T[]) => void;
  columns: ColumnDefinition<T>[];
  title?: string;
  uploadLabel?: string;
  uploadSublabel?: string;
  invalidItemRenderer?: (item: T, index: number) => React.ReactNode;
  errorMessage?: string;
  successMessage?: (count: number) => string;
}

function AutoshipUploadFileModal<T>(props: AutoshipUploadFileModalProps<T>) {
  const { t } = useTranslation('autoshipment');
  const [invalidItems, setInvalidItems] = useState<T[]>([]);
  const [validItems, setValidItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const handleFileSelected = async (file: File) => {
    setInvalidItems([]);
    setValidItems([]);
    setIsLoading(true);

    try {
      const result = await props.onFileSelected?.(file);

      if (result && !result.success) {
        setInvalidItems(result.data || []);
      } else if (result?.success) {
        setValidItems(result.data || []);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setValidItems([]);
    setInvalidItems([]);
    setIsLoading(false);
    props.onClose();
  };

  const handleConfirm = () => {
    props.onConfirm?.(validItems);
    handleClose();
  };

  return (
    <Dialog
      maxWidth='md'
      fullWidth
      onClose={props.onClose}
      aria-labelledby="customized-dialog-title"
      open={props.open}
    >
      <DialogTitle>
        { validItems.length > 0 ? t('step2Title') : t('step1Title') }
        <CloseDialogAction onCancel={handleClose} />
      </DialogTitle>
      <DialogContent
        sx={{ p: 0, pb: 5, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          py: 5,
          px: 8,
          width: '100%',
          textAlign: 'left',
          height: '100%',
          position: 'relative',
        }}> 
          {validItems?.length === 0 && <DragDropFileUpload
            acceptedFileTypes={['.csv', 'text/csv']}
            onFileSelected={handleFileSelected}
            maxFileSizeMB={10}
            label={props.uploadLabel || t('dragDropFileHere')}
            sublabel={props.uploadSublabel || t('orClickToBrowse')}
          />}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 1,
              }}
            >
              <LoadingWheel />
            </Box>
          )}
          {validItems?.length === 0 && invalidItems && (
            <Box sx={{ mt: 2, py: 5 }}>
              <Typography color="error">
                {props.errorMessage}
              </Typography>
              <List sx={{ color: 'error.main', mt: 1, pl: 6, listStyleType: 'disc' }}>
                {invalidItems?.map((item, index) => (
                  <ListItem key={index} 
                    sx={{ fontSize: '14px', display: 'list-item', padding: 0, pl: 0.5 }}>
                    {props.invalidItemRenderer 
                      ? props.invalidItemRenderer(item, index)
                      : JSON.stringify(item)
                    }
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {validItems?.length > 0 && (
            <Box>
              <Typography 
                variant='textSM'
                sx={{ mb: 2, mt: 1, color: 'success.main', fontWeight: 500 }}>
                {props.successMessage 
                  ? props.successMessage(validItems.length)
                  : `${validItems.length} ${t('recordsToUpload')}`
                }
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    {props.columns.map((column, index) => (
                      <TableCell key={index}>{column.header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validItems.map((item, index) => (
                    <TableRow key={index}>
                      {props.columns.map((column, colIndex) => (
                        <TableCell key={colIndex}>
                          {column.accessor(item)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </DialogContent>
      {validItems?.length > 0 &&
        <DialogActions>
          <Button 
            variant='secondary'
            onClick={handleClose}
            size='lg'>
            { t('cancel') }
          </Button>
          <Button 
            variant='primary'
            onClick={handleConfirm}
            size='lg'>
            { t('confirm') }
          </Button>
        </DialogActions>
      }
    </Dialog>
  );
}

export default AutoshipUploadFileModal;